import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { generateUUID } from "@/lib/utils";
import { useCompanies, type Company } from "@/hooks/useCompanies";

export type EquipmentInstanceStatus = "active" | "repair" | "archived";

export type EquipmentInstance = {
  id: string;
  equipment_id: string;
  identifier: string;
  notes: string | null;
  acquisition_date: string | null;
  acquisition_price: number | null;
  vendor_id: string | null;
  status: EquipmentInstanceStatus;
  created_at: string | null;
  updated_at: string | null;
  qr_code: string | null;
  serial_number: string | null;
  vendor?: Company | null;
};

export type EquipmentInstanceInsert = {
  id?: string;
  equipment_id: string;
  identifier: string;
  notes?: string | null;
  acquisition_date?: string | null;
  acquisition_price?: number | null;
  vendor_id?: string | null;
  status?: EquipmentInstanceStatus;
  qr_code?: string | null;
  serial_number?: string | null;
};
export type EquipmentInstanceUpdate = Partial<
  Omit<EquipmentInstanceInsert, "id" | "equipment_id">
>;

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroEquipmentInstanceRow = {
  id: string;
  equipmentID: string;
  identifier: string;
  notes?: string | null;
  acquisitionDate?: number | string | null;
  acquisitionPrice?: number | null;
  vendorID?: string | null;
  status: EquipmentInstanceStatus;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  qrCode?: string | null;
  serialNumber?: string | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? null;
}

function toLegacyDate(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? null;
}

function toLegacyInstance(
  row: ZeroEquipmentInstanceRow,
  vendor?: Company | null,
): EquipmentInstance {
  return {
    id: row.id,
    equipment_id: row.equipmentID,
    identifier: row.identifier,
    notes: row.notes ?? null,
    acquisition_date: toLegacyDate(row.acquisitionDate),
    acquisition_price: row.acquisitionPrice ?? null,
    vendor_id: row.vendorID ?? null,
    status: row.status,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    qr_code: row.qrCode ?? null,
    serial_number: row.serialNumber ?? null,
    vendor: vendor ?? null,
  };
}

function toZeroInstanceInput(
  instance: EquipmentInstanceInsert | EquipmentInstanceUpdate,
) {
  return {
    ...("equipment_id" in instance && instance.equipment_id !== undefined
      ? { equipmentID: instance.equipment_id }
      : {}),
    ...(instance.identifier !== undefined
      ? { identifier: instance.identifier }
      : {}),
    ...(instance.notes !== undefined ? { notes: instance.notes } : {}),
    ...(instance.acquisition_date !== undefined
      ? { acquisitionDate: instance.acquisition_date }
      : {}),
    ...(instance.acquisition_price !== undefined
      ? { acquisitionPrice: instance.acquisition_price }
      : {}),
    ...(instance.vendor_id !== undefined
      ? { vendorID: instance.vendor_id }
      : {}),
    ...(instance.status !== undefined ? { status: instance.status } : {}),
    ...(instance.qr_code !== undefined ? { qrCode: instance.qr_code } : {}),
    ...(instance.serial_number !== undefined
      ? { serialNumber: instance.serial_number }
      : {}),
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  success,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  success: string;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);
  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };
  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => {
          toast.success(success);
          options?.onSuccess?.(data);
        })
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

function useVendorMap() {
  const { data: companies = [] } = useCompanies();
  return useMemo(
    () => new Map(companies.map((company) => [company.id, company])),
    [companies],
  );
}

export const useEquipmentInstances = (equipmentId: string | undefined) => {
  const query = equipmentId
    ? appQueries.equipmentInstances.byEquipment({ equipmentID: equipmentId })
    : appQueries.equipmentInstances.byEquipment({
        equipmentID: "00000000-0000-0000-0000-000000000000",
      });
  const [data, details] = useZeroQuery(query, { enabled: !!equipmentId });
  const vendorMap = useVendorMap();
  const instances = ((data ?? []) as ZeroEquipmentInstanceRow[]).map((row) =>
    toLegacyInstance(row, row.vendorID ? vendorMap.get(row.vendorID) : null),
  );

  return {
    data: instances,
    isLoading:
      !!equipmentId && details.type !== "complete" && instances.length === 0,
    isFetching: !!equipmentId && details.type !== "complete",
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useBatchEquipmentInstances = (equipmentIds: string[]) => {
  const queryIds =
    equipmentIds.length > 0
      ? equipmentIds
      : ["00000000-0000-0000-0000-000000000000"];
  const [data, details] = useZeroQuery(
    appQueries.equipmentInstances.byEquipmentIDs({ equipmentIDs: queryIds }),
    { enabled: equipmentIds.length > 0 },
  );
  const vendorMap = useVendorMap();

  const grouped = useMemo(() => {
    const result: Record<string, EquipmentInstance[]> = {};
    for (const row of (data ?? []) as ZeroEquipmentInstanceRow[]) {
      const instance = toLegacyInstance(
        row,
        row.vendorID ? vendorMap.get(row.vendorID) : null,
      );
      if (!result[instance.equipment_id]) result[instance.equipment_id] = [];
      result[instance.equipment_id].push(instance);
    }
    return result;
  }, [data, vendorMap]);

  return {
    data: grouped,
    isLoading: equipmentIds.length > 0 && details.type !== "complete",
    isFetching: equipmentIds.length > 0 && details.type !== "complete",
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useCreateEquipmentInstance = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<EquipmentInstanceInsert, EquipmentInstance>({
    success: "Instance created",
    failure: "Failed to create instance",
    run: async (instance) => {
      const id = instance.id ?? generateUUID();
      const mutation = zero.mutate(
        appMutators.equipmentInstances.create({
          id,
          ...toZeroInstanceInput(instance),
          equipmentID: instance.equipment_id,
          identifier: instance.identifier,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment instance create rejected",
      );
      return toLegacyInstance({
        id,
        equipmentID: instance.equipment_id,
        identifier: instance.identifier,
        status: instance.status ?? "active",
        ...toZeroInstanceInput(instance),
      } as ZeroEquipmentInstanceRow);
    },
  });
};

export const useUpdateEquipmentInstance = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    EquipmentInstanceUpdate & { id: string; equipmentId: string },
    { id: string; equipmentId: string }
  >({
    success: "Instance updated",
    failure: "Failed to update instance",
    run: async ({ id, equipmentId, ...updates }) => {
      const mutation = zero.mutate(
        appMutators.equipmentInstances.update({
          id,
          ...toZeroInstanceInput(updates),
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment instance update rejected",
      );
      return { id, equipmentId };
    },
  });
};

export const useDeleteEquipmentInstance = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { id: string; equipmentId: string },
    { equipmentId: string }
  >({
    success: "Instance deleted",
    failure: "Failed to delete instance",
    run: async ({ id, equipmentId }) => {
      const mutation = zero.mutate(
        appMutators.equipmentInstances.delete({ id }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment instance delete rejected",
      );
      return { equipmentId };
    },
  });
};
